import { useCallback } from 'react'

import type { one_to_one } from '@fireworkhq/proto-contract'
import { useAppointment } from '@liveHelper/context/AppointmentContext/AppointmentContext'
import {
  actionsSelector,
  conversationSelector,
  hasSalesAgentMessagesInConversation,
  monitoringActionsSelector,
  monitoringSelector,
  paramsSelector,
  useStoreApi,
  visitorPrivateActionsSelector,
} from '@liveHelper/models/store'
import { customLiveHelperEvent } from '@liveHelper/tracking/customLiveHelperEvent'
import { EventType } from '@liveHelper/tracking/eventType'
import {
  useTrackJoinConversation,
  useTrackReceiveAIMessage,
} from '@liveHelper/tracking/useLiveHelperTracking'
import { startCall } from '@liveHelper/utils/videoCall'
import { TRANSFERRING_MESSAGE_REF } from '@oneToOne/shared/components/chat/constants'
import { playNotification } from '@oneToOne/shared/utils/audio'

import { isCurrentDomain } from '@core/helpers/isCurrentDomain'
import { uuidv4 } from '@core/helpers/uuidv4'
import { getBroadcastChannel } from '@core/hooks/useBroadcastChannel'
import { useIsFocusedTab } from '@core/hooks/useIsFocusedTab'
import { useContextTranslation } from '@core/i18n'
import { logError } from '@core/logging'

import type { VisitorChannel } from './types'

/** Hook to handle server pushed messages on visitor channel. */
export function useHandleVisitorMessages() {
  const handleCallMessages = useHandleCallMessages()
  const handleMonitorMessages = useHandleMonitorMessages()
  const handleNavigateMessages = useHandleNavigateMessages()
  const handleConversationMessages = useHandleConversationMessages()
  const handleQueueUpdate = useHandleQueueUpdate()

  return useCallback(
    (channel: VisitorChannel | undefined) => {
      const unsubscribers: Array<() => void> = []

      unsubscribers.push(handleCallMessages(channel))
      unsubscribers.push(handleMonitorMessages(channel))
      unsubscribers.push(handleNavigateMessages(channel))
      unsubscribers.push(handleConversationMessages(channel))
      unsubscribers.push(handleQueueUpdate(channel))

      return () => {
        unsubscribers.forEach((unsubscribe) => unsubscribe())
      }
    },
    [
      handleCallMessages,
      handleConversationMessages,
      handleMonitorMessages,
      handleNavigateMessages,
      handleQueueUpdate,
    ],
  )
}

function useHandleCallMessages() {
  const storeApi = useStoreApi()

  return useCallback(
    (channel: VisitorChannel | undefined) => {
      const actions = actionsSelector(storeApi.getState())
      const send = actions.sendUIStateTransitionEvent

      const incomingCallRef = channel?.on('incoming_call', (payload) => {
        const videoCall = payload.video_call
        if (startCall(videoCall, 'audience', storeApi)) {
          playNotification()
          send('RECEIVE_CALL')
        }
      })

      const callTakenRef = channel?.on('call_taken', (payload) => {
        const videoCall = payload.video_call
        if (startCall(videoCall, 'host', storeApi)) {
          send('START_CALL')

          // Putting this event in tasks to ensure that `new_conversation` event is handled properly with new conversation information
          setTimeout(() => {
            const liveHelperState = storeApi.getState()
            customLiveHelperEvent({
              type: EventType.FW_LIVE_HELPER_AGENT_ANSWER_CALL,
              data: {
                widget_id: paramsSelector(liveHelperState).widget_id,
                agent_id: conversationSelector(liveHelperState)?.agent?.id,
                agent_name: conversationSelector(liveHelperState)?.agent?.name,
              },
            })
          }, 0)
        }
      })

      const callTerminatedRef = channel?.on('call_terminated', () => {
        send('TERMINATE_CALL')
        actions.terminateCall()
      })

      const agentRequestedChannelAccessRef = channel?.on(
        'agent_requested_camera_access',
        (payload) => {
          send('START_CHAT')
          actions.setCameraAccessAllowedForVideoCallId(payload.video_call_id)
        },
      )

      return () => {
        channel?.off('incoming_call', incomingCallRef)
        channel?.off('call_taken', callTakenRef)
        channel?.off('call_terminated', callTerminatedRef)
        channel?.off(
          'agent_requested_camera_access',
          agentRequestedChannelAccessRef,
        )
      }
    },
    [storeApi],
  )
}

function useHandleMonitorMessages() {
  const storeApi = useStoreApi()

  return useCallback(
    (channel: VisitorChannel | undefined) => {
      const startMonitorRef = channel?.on('start_monitor', () => {
        const actions = actionsSelector(storeApi.getState())
        const monitoringActions = monitoringActionsSelector(storeApi.getState())

        getBroadcastChannel('monitoring').postMessage({
          event: 'start_monitor',
        })
        if (monitoringSelector(storeApi.getState())) {
          monitoringActions.takeFullSnapshot?.()
        } else {
          actions.setIsMonitoring(true)
        }
      })

      const stopMonitorRef = channel?.on('stop_monitor', () => {
        const actions = actionsSelector(storeApi.getState())
        getBroadcastChannel('monitoring').postMessage({ event: 'stop_monitor' })
        actions.setIsMonitoring(false)
      })

      return () => {
        channel?.off('start_monitor', startMonitorRef)
        channel?.off('stop_monitor', stopMonitorRef)
      }
    },
    [storeApi],
  )
}

function useHandleNavigateMessages() {
  const storeApi = useStoreApi()
  const isFocusedTab = useIsFocusedTab()

  return useCallback(
    (channel: VisitorChannel | undefined) => {
      const navigateRef = channel?.on('navigate', (payload) => {
        const { url, agent_id } = payload
        const conversation = conversationSelector(storeApi.getState())
        const { sendUIStateTransitionEvent, setRedirectUrl } = actionsSelector(
          storeApi.getState(),
        )

        if (conversation?.agent_id === agent_id && url) {
          if (isFocusedTab) {
            if (isCurrentDomain(url)) {
              sendUIStateTransitionEvent('MINIMIZE')
              setRedirectUrl(url)
            } else {
              window.open(url, '_blank')
            }
          } else {
            getBroadcastChannel('monitoring').postMessage({
              event: 'navigate',
              url,
            })
          }
        } else {
          logError(
            new Error(
              `Navigate request received from agent that is not in the conversation.`,
            ),
            {
              captureContext: {
                extra: {
                  agentIDInConversation: conversation?.agent_id,
                  agentIDInNavigateMessage: agent_id,
                },
              },
            },
          )
        }
      })

      return () => {
        channel?.off('navigate', navigateRef)
      }
    },
    [isFocusedTab, storeApi],
  )
}

function useHandleConversationMessages() {
  const storeApi = useStoreApi()
  const trackJoinConversation = useTrackJoinConversation()
  const trackReceiveAIMessage = useTrackReceiveAIMessage()
  const { t } = useContextTranslation()
  const { appointment, refetchAppointment } = useAppointment()

  return useCallback(
    (channel: VisitorChannel | undefined) => {
      const actions = actionsSelector(storeApi.getState())
      const params = paramsSelector(storeApi.getState())

      const newConversationRef = channel?.on('new_conversation', (payload) => {
        const { conversation } = payload
        if (conversation) {
          actions.setConversation(conversation)
          trackJoinConversation({ conversation, visitorInitiated: false })
          customLiveHelperEvent({
            type: EventType.FW_LIVE_HELPER_START_CONVERSATION,
            data: {
              widget_id: params?.widget_id,
            },
          })
        }
      })

      const terminatedConversationRef = channel?.on(
        'conversation_terminated',
        (payload) => {
          const { conversation } = payload
          if (conversation) {
            actions.removeConversation()
            if (conversation.intent !== 'CONVERSATION_INTENT_POST_SALES') {
              const message: one_to_one.shared.Message = {
                client_msg_ref: uuidv4(),
                created_at: new Date().toISOString(),
                system_message: {
                  content: t('oneToOne:Conversation ended'),
                  type: 'SYSTEM_MESSAGE_TYPE_INFO',
                },
              }
              actions.addChatMessages([message], 'visitor')
              if (
                !hasSalesAgentMessagesInConversation(conversation)(
                  storeApi.getState(),
                )
              ) {
                actions.sendUIStateTransitionEvent({
                  type: 'END_CONVERSATION',
                  isAppointment: !!appointment,
                  isActiveConversation: !!conversation.agent_id,
                })
              }
              if (appointment) {
                refetchAppointment?.()
              }
            }
          }
        },
      )

      const convesationIntentRef = channel?.on(
        'conversation_intent',
        (payload) => {
          const { conversation, customer_support_message: supportMessage } =
            payload

          if (conversation && conversation.id && supportMessage) {
            if (conversation.intent === 'CONVERSATION_INTENT_POST_SALES') {
              actions.addChatMessages([supportMessage], 'visitor')
              trackReceiveAIMessage({
                type: 'post_sales_classification_question',
                messageId: supportMessage.id,
                content: supportMessage.text_message?.content,
              })
              actions.addChatActions(conversation.id)

              const currentConversation = conversationSelector(
                storeApi.getState(),
              )
              actions.setConversation({
                ...currentConversation,
                intent: conversation.intent,
              })
            }
          }
        },
      )

      const conversationTransferedRef = channel?.on(
        'conversation_transferred',
        (payload) => {
          const message: one_to_one.shared.Message = {
            client_msg_ref: TRANSFERRING_MESSAGE_REF,
            created_at: new Date().toISOString(),
            system_message: {
              content: t('oneToOne:Transferring...'),
              type: 'SYSTEM_MESSAGE_TYPE_INFO',
            },
          }
          actions.addChatMessages([message], 'visitor')
          actions.setIsTransferring(true)

          const visitorPrivateActions = visitorPrivateActionsSelector(
            storeApi.getState(),
          )
          if (payload.in_video_call) {
            visitorPrivateActions.callAgent?.({
              isTransferredConversation: true,
            })
          }
        },
      )

      return () => {
        channel?.off('new_conversation', newConversationRef)
        channel?.off('conversation_terminated', terminatedConversationRef)
        channel?.off('conversation_intent', convesationIntentRef)
        channel?.off('conversation_transferred', conversationTransferedRef)
      }
    },
    [
      storeApi,
      trackJoinConversation,
      t,
      trackReceiveAIMessage,
      appointment,
      refetchAppointment,
    ],
  )
}

const useHandleQueueUpdate = () => {
  const storeApi = useStoreApi()
  const { appointment } = useAppointment()
  return useCallback(
    (channel: VisitorChannel | undefined) => {
      const queueUpdateRef = channel?.on('queue_update', (payload) => {
        if (payload?.queue_update?.position && !appointment) {
          storeApi
            .getState()
            .actions.setQueuePosition(payload?.queue_update?.position)
        }
      })
      return () => {
        channel?.off('queue_update', queueUpdateRef)
      }
    },
    [storeApi, appointment],
  )
}
